import React from 'react';
import {
  AlertCircle,
  CalendarX,
  UserX,
  RefreshCcw,
  ArrowRightLeft,
  ShieldOff,
  Ban,
  Clock,
  Info,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { DelegationApiError, DelegationErrorCode, DelegationValidationResult } from '../types/delegation.types';

// =============================================================================
// FE-19 — Validation Error Display
// Standardised error display for all delegation conflict error codes (PRD §5).
// Accepts either a DelegationApiError (from API) or a DelegationValidationResult
// (from client-side validation) and renders a consistent, human-readable alert.
// =============================================================================

// ---------------------------------------------------------------------------
// Error code → display config
// ---------------------------------------------------------------------------

interface ErrorDisplay {
  title: string;
  description: string;
  icon: React.ElementType;
}

const ERROR_CODE_MAP: Record<DelegationErrorCode, ErrorDisplay> = {
  self_delegation: {
    title: 'Cannot delegate to yourself',
    description:
      'The selected delegate is the same as your own account. Please choose a different colleague.',
    icon: UserX,
  },
  invalid_date_range: {
    title: 'Invalid date range',
    description:
      'The end date must be after the start date. Please check the dates you have entered.',
    icon: CalendarX,
  },
  overlap_conflict: {
    title: 'Overlapping delegation exists',
    description:
      'You already have an active delegation that covers some or all of this date range. Your organisation does not permit overlapping delegations. Please choose a different date range or revoke the existing delegation first.',
    icon: CalendarX,
  },
  circular_delegation: {
    title: 'Circular delegation detected',
    description:
      'The selected delegate has already delegated their approvals to you (or to someone in your delegation chain). A circular delegation chain would be created. Please choose a different delegate.',
    icon: ArrowRightLeft,
  },
  cascade_not_allowed: {
    title: 'Cascade delegation not permitted',
    description:
      'Your organisation does not allow delegates to further delegate their responsibilities. The selected person is already acting as a delegate for someone else. Please select a different delegate.',
    icon: RefreshCcw,
  },
  delegate_not_eligible: {
    title: 'Delegate is not eligible',
    description:
      'The selected colleague does not have the approval permissions required to act as a delegate. Please choose someone with approval rights, or contact your administrator.',
    icon: ShieldOff,
  },
  exceeds_max_duration: {
    title: 'Delegation period too long',
    description:
      'The selected date range exceeds the maximum delegation duration allowed by your organisation. Please shorten the delegation period.',
    icon: Clock,
  },
  delegation_disabled: {
    title: 'Approval delegation is disabled',
    description:
      'Approval delegation has been turned off by your administrator. Please contact your administrator to enable this feature.',
    icon: Ban,
  },
  not_found: {
    title: 'Delegation not found',
    description:
      'The delegation you are trying to update could not be found. It may have already been deleted or expired.',
    icon: AlertCircle,
  },
  already_actioned: {
    title: 'Already actioned',
    description:
      'This delegation has already been revoked or expired. No further action is required.',
    icon: Info,
  },
  forbidden: {
    title: 'Permission denied',
    description:
      'You do not have permission to perform this action. If you believe this is an error, please contact your administrator.',
    icon: ShieldOff,
  },
};

// ---------------------------------------------------------------------------
// Fallback for unknown / generic errors
// ---------------------------------------------------------------------------

const FALLBACK_ERROR: ErrorDisplay = {
  title: 'Something went wrong',
  description: 'An unexpected error occurred. Please try again. If the problem persists, contact support.',
  icon: AlertCircle,
};

// ---------------------------------------------------------------------------
// Helper: resolve display config for an error code
// ---------------------------------------------------------------------------

function resolveDisplay(code: DelegationErrorCode, overrideMessage?: string): ErrorDisplay {
  const base = ERROR_CODE_MAP[code] ?? FALLBACK_ERROR;
  if (overrideMessage) {
    return { ...base, description: overrideMessage };
  }
  return base;
}

// ---------------------------------------------------------------------------
// Sub-component: single error code alert
// ---------------------------------------------------------------------------

interface SingleErrorAlertProps {
  code: DelegationErrorCode;
  /** Override the default description with the server-supplied message */
  serverMessage?: string;
  /** Use 'destructive' for hard errors (default), or 'default' for warnings */
  variant?: 'destructive' | 'default';
  className?: string;
}

export const DelegationSingleError: React.FC<SingleErrorAlertProps> = ({
  code,
  serverMessage,
  variant = 'destructive',
  className,
}) => {
  const display = resolveDisplay(code, serverMessage);
  const Icon = display.icon;

  return (
    <Alert variant={variant} className={className}>
      <Icon className="h-4 w-4" />
      <AlertTitle>{display.title}</AlertTitle>
      <AlertDescription>{display.description}</AlertDescription>
    </Alert>
  );
};

// ---------------------------------------------------------------------------
// Main export: DelegationValidationError
//
// Accepts one of:
//   - `apiError`        — a DelegationApiError from a caught API call
//   - `validationResult`— a DelegationValidationResult from client-side validate()
//   - `message`         — a plain string (fallback for generic errors)
//
// If none are provided, renders nothing.
// ---------------------------------------------------------------------------

interface DelegationValidationErrorProps {
  /** Structured API error (e.g. from catch block after delegationService call) */
  apiError?: DelegationApiError | null;
  /** Client-side validation result — only shows the first error (to avoid overwhelming) */
  validationResult?: DelegationValidationResult | null;
  /** Plain string fallback for errors that didn't come from the delegation service */
  message?: string | null;
  /** Override Alert variant (default: destructive) */
  variant?: 'destructive' | 'default';
  className?: string;
  /** When true, shows ALL validation errors instead of just the first */
  showAllValidationErrors?: boolean;
}

export const DelegationValidationError: React.FC<DelegationValidationErrorProps> = ({
  apiError,
  validationResult,
  message,
  variant = 'destructive',
  className,
  showAllValidationErrors = false,
}) => {
  // --- API error takes priority ---
  if (apiError) {
    return (
      <DelegationSingleError
        code={apiError.error}
        serverMessage={apiError.message}
        variant={variant}
        className={className}
      />
    );
  }

  // --- Validation result (client-side) ---
  if (validationResult && !validationResult.valid && validationResult.errors.length > 0) {
    const errorsToShow = showAllValidationErrors
      ? validationResult.errors
      : [validationResult.errors[0]];

    if (errorsToShow.length === 1) {
      const err = errorsToShow[0];
      return (
        <DelegationSingleError
          code={err.code}
          serverMessage={err.message}
          variant={variant}
          className={className}
        />
      );
    }

    // Multiple errors: render as a list inside one alert
    return (
      <Alert variant={variant} className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Please fix the following errors</AlertTitle>
        <AlertDescription>
          <ul className="mt-1 list-disc list-inside space-y-0.5 text-sm">
            {errorsToShow.map((err, i) => (
              <li key={i}>{err.message}</li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>
    );
  }

  // --- Plain string fallback ---
  if (message) {
    return (
      <Alert variant={variant} className={className}>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Utility: human-readable label for any error code (for inline use outside Alert)
// ---------------------------------------------------------------------------

export function getDelegationErrorMessage(code: DelegationErrorCode): string {
  return ERROR_CODE_MAP[code]?.description ?? FALLBACK_ERROR.description;
}

export function getDelegationErrorTitle(code: DelegationErrorCode): string {
  return ERROR_CODE_MAP[code]?.title ?? FALLBACK_ERROR.title;
}
